import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

import { formatRating } from "@/modules/achievements/utils/formatRating";
import { StarsRating } from "@/modules/theme/components/Stars/StarsRating";
import { useTheme } from "@/modules/theme/hooks/useTheme";
import { grey } from "@/modules/theme/utils/colors";
import { Theme } from "@/modules/theme/utils/createTheme";
import { Achievement } from "@/modules/user/types";

type AchievementDataProps = {
  loading: boolean;
  currentAchievements: Achievement | null;
  className?: string;
  pointsWrapperClassName?: string;
};

const translationNamespace =
  "layouts/AuthenticatedLayout/components/AchievementData";

export const AchievementData: React.FC<AchievementDataProps> = ({
  loading,
  currentAchievements,
  className,
  pointsWrapperClassName,
}) => {
  const classes = createAchievementDataStyles(useTheme());
  const { t } = useTranslation(translationNamespace);
  return (
    <div className={cx(classes.root, className)}>
      {loading ? (
        <Skeleton height={14} width={200} />
      ) : (
        currentAchievements && (
          <>
            <div className={classes.dataWrapper}>
              <span>{t("Level")}</span>
              <span className={classes.dataItem}>
                {currentAchievements.levelOrder !== null
                  ? currentAchievements.levelOrder + 1
                  : "-"}
              </span>
            </div>
            <div className={cx(classes.dataWrapper, pointsWrapperClassName)}>
              <span>{t("Points")}</span>
              <span className={classes.dataItem}>
                {currentAchievements.levelOrder !== null
                  ? currentAchievements.pointsNumber
                  : "-"}
              </span>
            </div>
            <div className={classes.ratingWrapper}>
              <span>{formatRating(currentAchievements.averageRating)}</span>
              <StarsRating
                className={classes.rating}
                value={currentAchievements.averageRating}
                size={14}
              />
            </div>
          </>
        )
      )}
    </div>
  );
};

function createAchievementDataStyles({ typography, spacing }: Theme) {
  return {
    root: css({
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      fontSize: typography.pxToRem(13),
      fontWeight: typography.fontWeight.normal,
      marginTop: spacing(1),
      color: grey[800],
      width: "100%",
    }),
    dataWrapper: css({
      marginRight: spacing(2),
    }),
    dataItem: css({
      display: "inline-block",
      marginLeft: spacing(0.5),
      fontWeight: typography.fontWeight.semiBold,
    }),
    ratingWrapper: css({
      display: "flex",
      alignItems: "center",
    }),
    rating: css({
      marginLeft: spacing(0.5),
    }),
  };
}
